import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

import ColorPicker from '../../common/color-picker/ColorPicker';
import { deleteNode, updateNode } from '../../../services/board/board';
import { useCurrentBoardState } from '../../../services/state';
import { useNodeRecord } from '../../../services/deepstream';

import './UpdateCircle.css';
import '../../../App.css';

const dialogWidth = 300;
const dialogHeight = 365;
const headerHeightInPx = 70;
const defaultColor = '#2EAAEA';

const UpdateCircle = () => {
  const [title, setTitle] = useState('');
  const [color, setColor] = useState(defaultColor);
  const [{ top, left, visibility }, setDialogStyle] = useState({ visibility: 'hidden' });
  const titleInputRef = useRef(null);

  const boardState = useCurrentBoardState();
  const { moveWithChildren, editedNodeId } = boardState.get();
  const editedNode = useNodeRecord(editedNodeId);

  useEffect(() => {
    if (!editedNode) {
      setDialogStyle({ visibility: 'hidden' });
      return;
    }

    setTitle(editedNode.title || '');
    setColor(editedNode.color || defaultColor);

    const { top, left } = calcPosition(editedNode.id);
    setDialogStyle({ top, left, visibility: 'visible' });

    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [editedNode]);

  const handleColorChange = color => {
    setColor(color);
  };

  const handleChangeInput = event => {
    setTitle(event.target.value);
  };

  const handleSave = async () => {
    await updateNode(editedNodeId, { color, title });
    closeDialog();
  };

  const handleDelete = () => {
    deleteNode({ nodeId: editedNodeId, withChildren: moveWithChildren });
    closeDialog();
  };

  const closeDialog = () => {
    boardState.editedNodeId.set(null);
  };

  if (!editedNodeId || !editedNode) return null;

  return (
    <div
      tabIndex={-1}
      style={{ position: 'absolute', outline: 'none', top, left, visibility }}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          handleSave(event);
        }
        if (event.key === 'Escape') {
          closeDialog();
        }
      }}
    >
      <div className="ChangeCircle m-2 text-left position-relative">
        <div className="position-absolute closeBtn" onClick={closeDialog}>
          <FaTimes />
        </div>
        <div className="d-flex justify-content-center text-center example-wrapper">
          <div>
            <ColorPicker onChange={handleColorChange} color={color} />
            {editedNode.role === 'circle' && (
              <div
                className="example circleExample d-flex flex-column justify-content-center"
                style={{ backgroundColor: color, color: '#fff' }}
              >
                <svg
                  className="svg exampleSvg"
                  width="200px"
                  height="200px"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <circle
                      r="100"
                      cx="100"
                      cy="100"
                      fill={color}
                      strokeWidth="3"
                      stroke={color}
                    ></circle>
                    {editedNode.background !== '' && (
                      <foreignObject x="1" y="1" width="198" height="198">
                        <div
                          className="objectfit"
                          style={{
                            backgroundImage: `url(${editedNode.background})`
                          }}
                        />
                      </foreignObject>
                    )}

                    <foreignObject x="5" y="80" width="190" height="100">
                      <div>
                        <input
                          ref={titleInputRef}
                          name="nameInput"
                          aria-label="circle title"
                          className="input w-100 mb-0 text-center"
                          type="text"
                          value={title}
                          placeholder="click here and type"
                          onChange={handleChangeInput}
                        />
                      </div>
                    </foreignObject>
                  </g>
                </svg>
              </div>
            )}
            {editedNode.role === 'group' && (
              <div className="example d-flex flex-column justify-content-center">
                <svg
                  className="svg exampleSvg"
                  width="200px"
                  height="200px"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <circle
                      r="100"
                      cx="100"
                      cy="100"
                      fill="transparent"
                      strokeWidth="3"
                      stroke={color}
                    ></circle>
                    <foreignObject x="5" y="80" width="190" height="100">
                      <div>
                        <input
                          ref={titleInputRef}
                          className="input w-100 mb-0 text-center"
                          type="text"
                          value={title}
                          placeholder="click here and type"
                          onChange={handleChangeInput}
                        />
                      </div>
                    </foreignObject>
                  </g>
                </svg>
              </div>
            )}
          </div>
        </div>

        <div className="text-center btn-cta-wrapper m-t-1">
          <button className="btn btn-primary m-r-2 p-x-2" aria-label="Save" onClick={handleSave}>
            Save
          </button>
          <button className="btn btn-secondary" aria-label="Delete" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const calcPosition = nodeId => {
  const domNode = document.getElementById(`graph-node-${nodeId}`);
  if (!domNode) return {};

  const { top, left, width, height } = domNode.getBoundingClientRect();

  const eventX = left + width / 2;
  const eventY = top + height / 2;

  const dialogLeft = eventX - dialogWidth / 2;
  const leftMax = window.innerWidth - dialogWidth;

  const dialogTop = eventY - dialogHeight / 2;
  const topMax = window.innerHeight - dialogHeight;

  return {
    left: fitBetweenLimits({ value: dialogLeft, min: 0, max: leftMax }),
    top: fitBetweenLimits({ value: dialogTop, min: headerHeightInPx, max: topMax })
  };
};

const fitBetweenLimits = ({ value, min, max }) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export default UpdateCircle;
